import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    FAQList,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';
import { IPAD_OFFLINE_NOTE, TOOLS_COMPARISON } from '@/data/pdfMarkupToolsComparison';

const PAGE = pageForRoute('/free-pdf-markup-tools');

const FAQ_ITEMS = [
    {
        question: 'Why no prices on this comparison?',
        answer: [
            'Pricing changes frequently and can vary by region and subscription term. For current pricing, visit each tool\'s official site. This comparison focuses on the features that matter for construction drawing markup.',
        ],
    },
    {
        question: 'Is PDF Markup Tool really free?',
        answer: [
            'Yes. Browser-based use is free with no account required. We don\'t charge for markup, export, or any core features.',
        ],
    },
    {
        question: 'What does "local processing" mean?',
        answer: [
            'Your PDF is processed in your browser on your device. Your files don\'t leave your machine. Some tools offer both local processing and optional cloud storage.',
        ],
    },
    {
        question: 'Can I use these tools on iPad?',
        answer: [
            'Most tools run in a browser on iPad if the browser is supported. iPad-specific support varies by tool, so check their official documentation.',
        ],
    },
];

function ComparisonTable() {
    const rows = TOOLS_COMPARISON;
    const criteria = [
        { key: 'accountRequired' as const, label: 'Account required to use' },
        { key: 'uploadOrLocal' as const, label: 'File upload or local processing' },
        { key: 'freeExportWatermark' as const, label: 'Free export watermark/limits' },
        { key: 'platforms' as const, label: 'Platforms supported' },
        { key: 'offlineSupport' as const, label: 'Works offline' },
    ];

    return (
        <div className="comparison-table" style={{ overflowX: 'auto', marginBottom: '2rem' }}>
            <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.95rem',
                lineHeight: '1.5',
            }}>
                <thead>
                    <tr style={{
                        backgroundColor: 'var(--bk-background)',
                        borderBottom: '2px solid var(--bk-line)',
                    }}>
                        <th style={{
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            borderRight: '1px solid var(--bk-line)',
                        }}>
                            Tool
                        </th>
                        {criteria.map((c) => (
                            <th
                                key={c.key}
                                style={{
                                    padding: '1rem',
                                    textAlign: 'left',
                                    fontWeight: '600',
                                    borderRight: '1px solid var(--bk-line)',
                                    minWidth: '200px',
                                }}
                            >
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((tool) => (
                        <tr key={tool.name} style={{
                            borderBottom: '1px solid var(--bk-line)',
                        }}>
                            <td style={{
                                padding: '1rem',
                                fontWeight: '500',
                                borderRight: '1px solid var(--bk-line)',
                                backgroundColor: 'var(--bk-hover)',
                            }}>
                                <a href={tool.url} style={{
                                    color: 'var(--bk-primary)',
                                    textDecoration: 'none',
                                }} target="_blank" rel="noopener noreferrer">
                                    {tool.name}
                                </a>
                            </td>
                            {criteria.map((c) => {
                                const cell = tool[c.key];
                                return (
                                    <td
                                        key={`${tool.name}-${c.key}`}
                                        style={{
                                            padding: '1rem',
                                            borderRight: '1px solid var(--bk-line)',
                                        }}
                                    >
                                        <div>
                                            {cell.value}
                                        </div>
                                        {cell.sourceUrl && (
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: 'var(--bk-muted)',
                                                marginTop: '0.25rem',
                                            }}>
                                                <a href={cell.sourceUrl} style={{
                                                    color: 'var(--bk-primary)',
                                                    textDecoration: 'none',
                                                }} target="_blank" rel="noopener noreferrer">
                                                    Verified {cell.checked}
                                                </a>
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            <p style={{
                fontSize: '0.9rem',
                color: 'var(--bk-muted)',
                marginTop: '1rem',
                fontStyle: 'italic',
            }}>
                &quot;--&quot; indicates the feature status is not stated on the tool&apos;s public pages.
                Comparison checked 2026-10-18.{' '}
                <span data-testid="ipad-home-screen-note">{IPAD_OFFLINE_NOTE}</span>
            </p>

        </div>
    );
}

export function FreePdfMarkupToolsPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="Free PDF Markup Tools"
                    title="Free PDF markup tools compared"
                    lead="See how PDF Markup Tool stacks up against other free tools for construction drawing review. Neutral comparison on features that matter: no account required, local processing, free export, cross-platform support, and offline use."
                    cta={<Button href="/" variant="primary" size="lg">Try PDF Markup Tool</Button>}
                />

                <section style={{ padding: '2rem 1rem', maxWidth: '1200px', margin: '0 auto' }}>
                    <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>
                        Feature Comparison
                    </h2>
                    <ComparisonTable />
                </section>

                <FAQList items={FAQ_ITEMS} />

                <CtaBand
                    title="Ready to mark up for free?"
                    lead="Open a PDF in your browser right now — no download, no signup, nothing uploaded."
                    actions={
                        <Button href="/" variant="primary" size="lg">
                            Start marking up
                        </Button>
                    }
                />
                <Dateline reviewed={PAGE.reviewed} />

                <PageFooter tagline="Free, no-account PDF markup for AEC professionals" />
            </main>
        </MarketingPage>
    );
}
